import React, { useState } from "react";
import "./AttendanceSummary.css";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Modal,
  TextField,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const initialData = [
  {
    id: 1,
    empId: "EMP001",
    name: "Rahul Kumar",
    workingDays: 26,
    presentDays: 22,
    leaves: 4,
    totalDays: 26,
    lop: 0,
    paidDays: 26,
    billableHours: 168,
  },
  {
    id: 2,
    empId: "EMP002",
    name: "Amit Singh",
    workingDays: 26,
    presentDays: 20,
    leaves: 6,
    totalDays: 26,
    lop: 2,
    paidDays: 24,
    billableHours: 160,
  },
];

const AttendanceSummary = () => {
  const [data, setData] = useState(initialData);
  const [year, setYear] = useState("2024");
  const [month, setMonth] = useState("January");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  /* IMPORT CSV */
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const rows = evt.target.result.split("\n").slice(1);
      const parsed = rows
        .map((r, i) => {
          const c = r.split(",");
          if (c.length < 9) return null;
          return {
            id: i + 1,
            empId: c[0],
            name: c[1],
            workingDays: +c[2],
            presentDays: +c[3],
            leaves: +c[4],
            totalDays: +c[5],
            lop: +c[6],
            paidDays: +c[7],
            billableHours: +c[8],
          };
        })
        .filter(Boolean);
      setData(parsed);
    };
    reader.readAsText(file);
  };

  /* EXPORT CSV */
  const handleExport = () => {
    const csv = [
      "EmpId,Name,WorkingDays,PresentDays,Leaves,TotalDays,LOP,PaidDays,BillableHours",
      ...data.map(
        (d) =>
          `${d.empId},${d.name},${d.workingDays},${d.presentDays},${d.leaves},${d.totalDays},${d.lop},${d.paidDays},${d.billableHours}`
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "attendance-summary.csv";
    a.click();
  };

  /* DELETE */
  const handleDelete = (id) => {
    if (window.confirm("Delete this record?")) {
      setData(data.filter((d) => d.id !== id));
    }
  };

  /* SAVE EDIT */
  const handleSave = () => {
    setData(data.map((d) => (d.id === selectedRow.id ? selectedRow : d)));
    setOpenEdit(false);
  };

  return (
    <Box className="attendance-wrapper">
      {/* FILTER BAR */}
      <Box className="attendance-filters">
        <FormControl size="small">
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
            <MenuItem value="2024">2024</MenuItem>
            <MenuItem value="2023">2023</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Month</InputLabel>
          <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
            {[
              "January","February","March","April","May","June",
              "July","August","September","October","November","December",
            ].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box className="attendance-actions">
          <input hidden type="file" accept=".csv" id="attendance-upload" onChange={handleImport} />
          <Button
            variant="contained"
            color="success"
            startIcon={<CloudUploadIcon />}
            onClick={() => document.getElementById("attendance-upload").click()}
          >
            Import
          </Button>

          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} className="attendance-table">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Working Days</TableCell>
              <TableCell>Present Days</TableCell>
              <TableCell>Leaves</TableCell>
              <TableCell>Total Days</TableCell>
              <TableCell>LOP</TableCell>
              <TableCell>Total Paid Days</TableCell>
              <TableCell>Billable Hours</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{row.empId}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.workingDays}</TableCell>
                  <TableCell>{row.presentDays}</TableCell>
                  <TableCell>{row.leaves}</TableCell>
                  <TableCell>{row.totalDays}</TableCell>
                  <TableCell>{row.lop}</TableCell>
                  <TableCell>{row.paidDays}</TableCell>
                  <TableCell>{row.billableHours}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => { setSelectedRow(row); setOpenView(true); }}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton color="warning" onClick={() => { setSelectedRow(row); setOpenEdit(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
        />
      </TableContainer>

      {/* VIEW MODAL */}
      <Modal open={openView} onClose={() => setOpenView(false)}>
        <Box className="attendance-modal">
          <IconButton className="attendance-close" onClick={() => setOpenView(false)}>
            <CloseIcon />
          </IconButton>
          <h3>Attendance Details</h3>

          {selectedRow && (
            <div className="attendance-view-grid">
              <div><span>Employee ID</span><strong>{selectedRow.empId}</strong></div>
              <div><span>Name</span><strong>{selectedRow.name}</strong></div>
              <div><span>Working Days</span><strong>{selectedRow.workingDays}</strong></div>
              <div><span>Present Days</span><strong>{selectedRow.presentDays}</strong></div>
              <div><span>Leaves</span><strong>{selectedRow.leaves}</strong></div>
              <div><span>Total Days</span><strong>{selectedRow.totalDays}</strong></div>
              <div><span>LOP</span><strong>{selectedRow.lop}</strong></div>
              <div><span>Paid Days</span><strong>{selectedRow.paidDays}</strong></div>
              <div><span>Billable Hours</span><strong>{selectedRow.billableHours}</strong></div>
            </div>
          )}
        </Box>
      </Modal>

      {/* EDIT MODAL */}
      <Modal open={openEdit} onClose={() => setOpenEdit(false)}>
        <Box className="attendance-modal">
          <IconButton className="attendance-close" onClick={() => setOpenEdit(false)}>
            <CloseIcon />
          </IconButton>
          <h3>Edit Attendance</h3>

          {selectedRow && (
            <div className="attendance-edit-grid">
              <TextField label="Employee ID" value={selectedRow.empId} disabled />
              <TextField label="Name" value={selectedRow.name}
                onChange={(e) => setSelectedRow({ ...selectedRow, name: e.target.value })} />
              <TextField label="Working Days" type="number" value={selectedRow.workingDays}
                onChange={(e) => setSelectedRow({ ...selectedRow, workingDays: e.target.value })} />
              <TextField label="Present Days" type="number" value={selectedRow.presentDays}
                onChange={(e) => setSelectedRow({ ...selectedRow, presentDays: e.target.value })} />
              <TextField label="Leaves" type="number" value={selectedRow.leaves}
                onChange={(e) => setSelectedRow({ ...selectedRow, leaves: e.target.value })} />
              <TextField label="LOP" type="number" value={selectedRow.lop}
                onChange={(e) => setSelectedRow({ ...selectedRow, lop: e.target.value })} />
              <TextField label="Paid Days" type="number" value={selectedRow.paidDays}
                onChange={(e) => setSelectedRow({ ...selectedRow, paidDays: e.target.value })} />
              <TextField label="Billable Hours" type="number" value={selectedRow.billableHours}
                onChange={(e) => setSelectedRow({ ...selectedRow, billableHours: e.target.value })} />

              <div className="attendance-edit-actions">
                <Button fullWidth variant="contained" onClick={handleSave}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default AttendanceSummary;
